import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { isSuperAdmin } from '../../lib/permissions';
import { LogOut, Sun, Moon, Menu, KeyRound, ShieldCheck, Search } from 'lucide-react';
import { getInitials } from '../../lib/utils';
import NotificationCenter from './NotificationCenter';
import QuickTimer from '../QuickTimer';
import Tooltip from '../Tooltip';
import ChangePasswordModal from '../ChangePasswordModal';
import MfaModal from '../MfaModal';

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isSuper = isSuperAdmin(user?.role, user?.is_super_admin);
  const [showPwd, setShowPwd] = useState(false);
  const [showMfa, setShowMfa] = useState(false);

  return (
    <header className="h-[64px] flex items-center justify-between px-3 md:px-6 border-b" style={{borderColor:'var(--border-color)', background:'var(--bg-secondary)', backdropFilter:'blur(12px)'}}>
      {/* Mobile Menu Button */}
      <button
        onClick={onMenuClick}
        className="md:hidden w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
        style={{background:'var(--bg-tertiary)'}}
        data-testid="mobile-menu-button"
      >
        <Menu className="w-5 h-5" style={{color:'var(--text-primary)'}} />
      </button>

      {/* Recherche (cabinet) — masquée pour l'administration plateforme */}
      {isSuper ? (
        <div className="hidden md:block font-serif text-[14px]" style={{ color: 'var(--text-secondary)' }}>Console plateforme</div>
      ) : (
        <button
          onClick={() => window.dispatchEvent(new Event('adalia:command-open'))}
          className="hidden md:flex items-center gap-2.5 w-[400px] px-3 py-2 rounded-[8px] text-[13px] transition-colors hover:border-[rgba(201,162,39,0.3)]"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-tertiary)' }}
          data-testid="command-palette-trigger"
        >
          <Search className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left">Rechercher ou lancer une action…</span>
          <kbd className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ border: '1px solid var(--border-color)' }}>⌘K</kbd>
        </button>
      )}

      {/* Right */}
      <div className="flex items-center gap-4">
        {!isSuper && <QuickTimer />}
        {/* Theme Toggle */}
        <Tooltip content={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'} position="bottom">
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
            style={{background:'var(--bg-tertiary)'}}
            data-testid="theme-toggle-button"
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4" style={{color:'var(--accent-gold)'}} />
            ) : (
              <Moon className="w-4 h-4" style={{color:'var(--accent-blue)'}} />
            )}
          </button>
        </Tooltip>

        {!isSuper && <NotificationCenter />}

        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-[13px] font-medium" style={{color:'var(--text-primary)'}}>{user?.nom || 'Utilisateur'}</div>
            <div className="text-[11px]" style={{color:'var(--text-tertiary)'}}>{user?.role || ''}</div>
          </div>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-medium"
            style={{ border: '1px solid var(--accent-gold)', color: 'var(--accent-gold)' }}
          >
            {getInitials(user?.nom)}
          </div>
          <Tooltip content="Sécurité (2FA)" position="bottom">
            <button onClick={() => setShowMfa(true)} className="p-2 rounded-lg transition-colors" style={{background:'var(--bg-tertiary)'}} data-testid="mfa-button">
              <ShieldCheck className="w-4 h-4" style={{color:'var(--text-tertiary)'}} />
            </button>
          </Tooltip>
          <Tooltip content="Changer mon mot de passe" position="bottom">
            <button onClick={() => setShowPwd(true)} className="p-2 rounded-lg transition-colors" style={{background:'var(--bg-tertiary)'}} data-testid="change-password-button">
              <KeyRound className="w-4 h-4" style={{color:'var(--text-tertiary)'}} />
            </button>
          </Tooltip>
          <Tooltip content="Se déconnecter" position="bottom">
            <button onClick={logout} className="p-2 rounded-lg transition-colors" style={{background:'var(--bg-tertiary)'}} data-testid="logout-button">
              <LogOut className="w-4 h-4" style={{color:'var(--text-tertiary)'}} />
            </button>
          </Tooltip>
        </div>
      </div>
      <ChangePasswordModal isOpen={showPwd} onClose={() => setShowPwd(false)} />
      <MfaModal isOpen={showMfa} onClose={() => setShowMfa(false)} />
    </header>
  );
}
